import { useReactiveVar } from '@apollo/client';
import { HomeOutlined } from '@mui/icons-material';
import ApiOutlined from '@mui/icons-material/ApiOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import BookOutlined from '@mui/icons-material/BookOutlined';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import SupervisorAccountOutlined from '@mui/icons-material/SupervisorAccountOutlined';
import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { type Location } from 'react-router';
import { authVar } from 'state/vars';
import { areQrCodeVisitsEnabled } from 'v2/utils/areQrCodeVisitsEnabled';

export interface MenuItem {
  location: string;
  text: string;
  icon: OverridableComponent<SvgIconTypeMap>;
  isSelected: boolean;
}

interface Props {
  translation: Translation;
  location: Location;
}

const isDashboardEnabled = process.env.REACT_APP_IS_DASHBOARD_ENABLED === 'true';

export const isPathMatch = (currentPath: string, targetPath: string) => {
  if (targetPath === '/') {
    return currentPath === '/';
  }
  return currentPath.startsWith(targetPath);
};

const useNavigationItems = ({ translation, location }: Props) => {
  const { user } = useReactiveVar(authVar);
  const isAdmin = Boolean(user?.isAdmin);

  const getNavigationItems = (): Array<MenuItem> => {
    const items: Array<Omit<MenuItem, 'isSelected'>> = [];

    if (isAdmin) {
      if (isDashboardEnabled) {
        items.push({
          location: '/dashboard',
          text: translation.home,
          icon: HomeOutlined
        });
      }
      items.push({
        location: '/users',
        text: translation.users,
        icon: SupervisorAccountOutlined
      });
      items.push({
        location: '/devices',
        text: translation.devices,
        icon: LockOutlined
      });
      items.push({
        location: '/cards',
        text: translation.cards,
        icon: CreditCardOutlined
      });
      items.push({
        location: '/events',
        text: translation.events,
        icon: BookOutlined
      });
      if (areQrCodeVisitsEnabled()) {
        items.push({
          location: '/visits',
          text: translation.visitors,
          icon: BadgeOutlined
        });
      }
      items.push({
        location: '/api-keys',
        text: translation.api_keys,
        icon: ApiOutlined
      });
    }

    const itemsWithSelected = items.map((item) => ({
      ...item,
      isSelected: isPathMatch(location.pathname, item.location)
    }));

    return itemsWithSelected;
  };

  return {
    getNavigationItems
  };
};

export default useNavigationItems;
